import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetBusiness } from "@/business/query";
import type { IUser } from "@/core/auth";
import { dayjs } from "@/core/utils/date";
import {
  buildPeriods,
  DEFAULT_TEMPLATE,
  hasActiveIcmRun,
} from "@/icm/icm-page/utils";
import { useGetAiScriptSpreadsheet } from "@/payroll/ai-script/query";
import {
  getAiScriptPayrollRunStatus,
  hasActiveAiScriptRun,
} from "@/payroll/ai-script/utils";
import { useSheetApprovalActions } from "@/payroll/approvals/use-sheet-approval-actions";
import { PAYROLL_RUN_STATUS } from "@/payroll/constants";
import { getRerunUnits } from "@/payroll/pay-breakdown/rerun-units";
import { getPayBreakdownPath } from "@/payroll/pay-breakdown/routes";
import { PAY_BREAKDOWN_RUN_TARGET_TYPE } from "@/payroll/pay-breakdown/types";
import {
  applyPayBreakdownStatusToPeriods,
  getLatestCompletedLegacyRun,
  getDefaultPayBreakdownPeriod,
  getPayBreakdownOutput,
  getPayPeriodsPollInterval,
  isFullWorkbookRun,
} from "@/payroll/pay-breakdown/utils";
import { canLockPayPeriod } from "@/payroll/pay-periods/pay-period-lock";
import {
  useGetPayPeriodDetail,
  useGetPayPeriods,
} from "@/payroll/pay-periods/query";
import type { EnrichedPayPeriod } from "@/payroll/payroll.d";
import {
  createCalculatedPayPeriod,
  isPayPeriodLocked,
} from "@/payroll/utils/period-helpers";

interface UsePayBreakdownPeriodParams {
  businessId: string | null;
  user: IUser | null;
  selectedPeriodStart: string | undefined;
}

export const usePayBreakdownPeriod = ({
  businessId,
  user,
  selectedPeriodStart,
}: UsePayBreakdownPeriodParams) => {
  const navigate = useNavigate();
  const { search } = useLocation();

  const { data: business, isLoading: isBusinessLoading } =
    useGetBusiness(businessId);
  const { data: payPeriods = [], isLoading: isPayPeriodsLoading } =
    useGetPayPeriods(businessId, {
      refetchInterval: (query) => getPayPeriodsPollInterval(query.state.data),
    });

  const selectedPayPeriod = payPeriods.find(
    (period) => period.start_date === selectedPeriodStart,
  );
  const isLocked = isPayPeriodLocked(selectedPayPeriod);
  const isFuturePeriod =
    !!selectedPeriodStart && dayjs(selectedPeriodStart).isAfter(dayjs(), "day");
  const showLockPayPeriod =
    !isFuturePeriod &&
    canLockPayPeriod({
      period: selectedPayPeriod,
      business,
      user,
    });
  const selectedAiRunId =
    selectedPayPeriod?.latest_completed_ai_script_run_id ?? null;
  const generatedWorkbookMetadata =
    selectedPayPeriod?.latest_completed_ai_script_run_generated_workbook_metadata ??
    null;
  const hasGeneratedWorkbook =
    !!selectedPayPeriod?.latest_completed_ai_script_run_generated_workbook_url ||
    !!generatedWorkbookMetadata;
  const selectedIcmRunId =
    selectedPayPeriod?.latest_completed_icm_run_id ?? null;

  const shouldFetchLegacyDetail =
    !!businessId &&
    !!selectedPayPeriod?.id &&
    !hasGeneratedWorkbook &&
    !selectedIcmRunId &&
    selectedPayPeriod.runs_count > 0;
  const {
    data: periodDetail,
    isLoading: isLegacyDetailLoading,
    isError: isPeriodDetailError,
    refetch: refetchPeriodDetail,
  } = useGetPayPeriodDetail(businessId, selectedPayPeriod?.id, {
    enabled: shouldFetchLegacyDetail || (!!businessId && !!selectedAiRunId),
    runId: selectedAiRunId,
  });
  const approvalStatus = isPeriodDetailError
    ? null
    : (periodDetail?.approval_status ?? null);
  const sheetApprovalActions = useSheetApprovalActions({
    businessId,
    periodId: selectedPayPeriod?.id,
    runId: selectedAiRunId,
    approvalStatus,
  });

  const latestLegacyRun = getLatestCompletedLegacyRun(
    shouldFetchLegacyDetail ? periodDetail?.payroll_runs : undefined,
  );

  const output = getPayBreakdownOutput({
    hasGeneratedWorkbook,
    selectedAiRunId,
    selectedIcmRunId,
    latestLegacyRun,
  });
  const aiScriptRunId =
    output.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript
      ? output.runId
      : null;

  const template = business?.current_pay_period_template ?? DEFAULT_TEMPLATE;
  const { periods: rawPeriods, calendarPeriods } = useMemo(
    () =>
      buildPeriods({
        template,
        payPeriods,
        isBusinessLoading,
        businessCreatedAt: business?.created_at,
        futurePeriods: business?.can_rerun_full_payroll === true ? 1 : 0,
      }),
    [
      template,
      payPeriods,
      isBusinessLoading,
      business?.created_at,
      business?.can_rerun_full_payroll,
    ],
  );
  const periods = useMemo(
    () => applyPayBreakdownStatusToPeriods(rawPeriods, payPeriods),
    [rawPeriods, payPeriods],
  );

  const isPeriodsLoading = isBusinessLoading || isPayPeriodsLoading;

  useEffect(() => {
    const defaultPeriod = getDefaultPayBreakdownPeriod(
      periods,
      selectedPeriodStart,
    );

    if (defaultPeriod) {
      navigate(
        {
          pathname: getPayBreakdownPath(defaultPeriod.period_start),
          search,
        },
        { replace: true },
      );
    }
  }, [navigate, periods, search, selectedPeriodStart]);

  const selectedPeriod = periods.find(
    (period) => period.period_start === selectedPeriodStart,
  );
  const selectedPeriodForCard: EnrichedPayPeriod | undefined = useMemo(() => {
    if (selectedPayPeriod) return selectedPayPeriod;
    if (!selectedPeriod) return undefined;

    return createCalculatedPayPeriod(
      dayjs(selectedPeriod.period_start),
      dayjs(selectedPeriod.period_end),
    );
  }, [selectedPayPeriod, selectedPeriod]);
  const hasActiveAiRun = hasActiveAiScriptRun(selectedPayPeriod);
  const hasActiveIcmPayrollRun = hasActiveIcmRun(selectedPayPeriod);
  const hasActiveLegacyRun =
    selectedPayPeriod?.latest_run_status === PAYROLL_RUN_STATUS.pending ||
    selectedPayPeriod?.latest_run_status === PAYROLL_RUN_STATUS.processing;
  const hasActiveRun =
    hasActiveAiRun || hasActiveIcmPayrollRun || hasActiveLegacyRun;
  const activeAiScriptRuns = selectedPayPeriod?.active_ai_script_runs ?? [];
  const busyRerunSheetNames = useMemo(
    () => activeAiScriptRuns.flatMap((run) => run.sheet_scope ?? []),
    [activeAiScriptRuns],
  );
  const hasActiveFullAiRun = activeAiScriptRuns.some(isFullWorkbookRun);
  const hasBlockingRun =
    hasActiveFullAiRun || hasActiveIcmPayrollRun || hasActiveLegacyRun;

  const { data: workbookData } = useGetAiScriptSpreadsheet(
    businessId,
    aiScriptRunId,
  );
  const rerunUnits = useMemo(
    () =>
      getRerunUnits({
        sheets: workbookData?.spreadsheet.sheets ?? [],
        approvalStatus,
        busySheetNames: busyRerunSheetNames,
      }),
    [workbookData?.spreadsheet.sheets, approvalStatus, busyRerunSheetNames],
  );
  const hasEditableSheet = useMemo(
    () =>
      (workbookData?.spreadsheet.sheets ?? []).some((sheet) => sheet.editable),
    [workbookData?.spreadsheet.sheets],
  );

  const selectedRunStatus =
    selectedPayPeriod?.latest_ai_script_run_status ?? null;
  const selectedPayrollRunStatus =
    getAiScriptPayrollRunStatus(selectedRunStatus);
  const icmLatestRunStatus = selectedPayPeriod?.latest_icm_run_status ?? null;

  const isBeforeBusinessCreated =
    !!selectedPeriodStart &&
    !!business?.created_at &&
    dayjs(selectedPeriodStart).isBefore(dayjs(business.created_at), "day");
  const periodNotFound =
    !!selectedPeriodStart &&
    !isPeriodsLoading &&
    periods.length > 0 &&
    !selectedPeriod;
  const periodReady =
    !!selectedPeriodStart &&
    !isPeriodsLoading &&
    !periodNotFound &&
    !hasActiveRun;

  return {
    business,
    isBusinessLoading,
    payPeriods,
    isPayPeriodsLoading,
    selectedPayPeriod,
    isLocked,
    isFuturePeriod,
    showLockPayPeriod,
    selectedAiRunId,
    sheetApprovalActions,
    generatedWorkbookMetadata,
    approvalStatus,
    output,
    aiScriptRunId,
    shouldFetchLegacyDetail,
    hasGeneratedWorkbook,
    periodDetail,
    isLegacyDetailLoading,
    isPeriodDetailError,
    refetchPeriodDetail,
    periods,
    calendarPeriods,
    isPeriodsLoading,
    selectedPeriod,
    selectedPeriodForCard,
    hasActiveIcmPayrollRun,
    hasActiveLegacyRun,
    hasActiveRun,
    activeAiScriptRuns,
    hasBlockingRun,
    workbookData,
    rerunUnits,
    hasEditableSheet,
    selectedRunStatus,
    selectedPayrollRunStatus,
    icmLatestRunStatus,
    isBeforeBusinessCreated,
    periodNotFound,
    periodReady,
  };
};

export type PayBreakdownPeriodState = ReturnType<typeof usePayBreakdownPeriod>;
